import { Crown } from "lucide-react";

/**
 * Elder badge — granted to the first 500 providers.
 * Renders a small gold crown inline; pass `index` to show their elder rank
 * via the title/tooltip ("#42 of 500").
 */
export default function ElderBadge({ size = 14, className = "", show = false, index }) {
  if (!show) return null;
  const label = index
    ? `Elder Practitioner · #${index} of 500`
    : "Elder Practitioner";
  return (
    <span
      title={label}
      data-testid="elder-badge"
      className={`inline-flex items-center justify-center text-primary ${className}`}
    >
      <Crown size={size} strokeWidth={1.75} className="fill-primary/30" />
    </span>
  );
}

/**
 * ElderAvatarRing — wraps an avatar with a gold dashed ring + crown corner.
 * Use around `<img>` / initial circle. Pass `show=true` only for elders.
 */
export function ElderAvatarRing({ show = false, index, children, className = "" }) {
  if (!show) return children;
  return (
    <div className={`relative inline-block ${className}`} data-testid="elder-avatar-ring">
      {/* Outer gold ring + inner amber ring — sits OUTSIDE the avatar's card border */}
      <div
        className="absolute pointer-events-none rounded-full"
        style={{
          inset: "-8px",
          background:
            "conic-gradient(from 140deg, #C5A059 0deg, #D95C14 90deg, #F4D58E 180deg, #C5A059 270deg, #D95C14 360deg)",
          padding: "3px",
          WebkitMask:
            "linear-gradient(#fff 0 0) content-box, linear-gradient(#fff 0 0)",
          WebkitMaskComposite: "xor",
          maskComposite: "exclude",
          boxShadow:
            "0 0 18px rgba(217,92,20,0.45), 0 0 32px rgba(245,213,142,0.25)",
          zIndex: 0,
        }}
      />
      {/* Avatar itself sits above the ring */}
      <div className="relative" style={{ zIndex: 1 }}>
        {children}
      </div>
      {/* Crown badge in the top-right corner — large enough to be unmistakable */}
      <span
        title={index ? `Elder Practitioner · #${index} of 500` : "Elder Practitioner"}
        className="absolute flex items-center justify-center rounded-full"
        style={{
          top: "-6px",
          right: "-6px",
          width: 28,
          height: 28,
          background: "linear-gradient(135deg, #D95C14 0%, #C5A059 60%, #F4D58E 100%)",
          color: "#15102A",
          boxShadow: "0 4px 12px rgba(0,0,0,0.4), 0 0 0 2px #15102A",
          zIndex: 2,
        }}
        data-testid="elder-avatar-crown"
      >
        <Crown size={14} strokeWidth={2.25} fill="currentColor" />
      </span>
    </div>
  );
}
